import React, { useState, useRef, useEffect } from 'react';
import { X, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LendBorrow, LendBorrowInput } from '../../types/index';
import { useFinanceStore } from '../../store/useFinanceStore';
import { toast } from 'sonner';
import { CustomDropdown } from '../Purchases/CustomDropdown';
// DatePicker loaded dynamically to reduce initial bundle size
// import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
import { LazyDayPicker as DatePicker } from '../common/LazyDayPicker';
import { parseLocalDate } from '../../utils/taskDateUtils';
import { useAuthStore } from '../../store/authStore';
import { Loader } from '../../components/common/Loader';
import { useLoadingContext } from '../../context/LoadingContext';
import { getCurrencySymbol } from '../../utils/currency';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { AmountAdjustmentModal } from '../common/AmountAdjustmentModal';
import { supabase } from '../../lib/supabase';
import { EyeOff } from 'lucide-react';

interface LendBorrowFormProps {
  record?: LendBorrow;
  onClose: () => void;
  onSubmit: (data: LendBorrowInput) => void;
}

export const LendBorrowForm: React.FC<LendBorrowFormProps> = ({ record, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const { accounts, lendBorrowRecords } = useFinanceStore();
  const { profile } = useAuthStore();
  const { isLoading } = useLoadingContext();
  const { isMobile } = useMobileDetection();
  const [form, setForm] = useState<LendBorrowInput>({
    type: record?.type || '',
    person_name: record?.person_name || '',
    amount: record?.amount || undefined,
    currency: record?.currency || '',
    due_date: record?.due_date || '',
    notes: record?.notes || '',
    status: record?.status || 'active',
    partial_return_amount: record?.partial_return_amount || 0,
    partial_return_date: record?.partial_return_date || '',
    // Only fall back to the default account when adding. A record-only entry has no
    // account, and injecting one here would overwrite its currency on edit.
    account_id: record ? (record.account_id || '') : (profile?.default_account_id || ''),
    affect_account_balance: record?.affect_account_balance ?? true,
  });
  
  // Store amount as string for adjust mode parsing
  const [amountInput, setAmountInput] = useState<string>(record?.amount ? String(record.amount) : '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const typeRef = useRef<HTMLInputElement | null>(null);
  const personNameRef = useRef<HTMLInputElement | null>(null);
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  // Amount adjustment mode state
  const [amountMode, setAmountMode] = useState<'set' | 'adjust'>('set');
  const [originalAmount, setOriginalAmount] = useState<number | null>(null);
  const [showAmountModal, setShowAmountModal] = useState(false);
  
  // Autocomplete state for person name
  const [showPersonNameSuggestions, setShowPersonNameSuggestions] = useState(false);
  const [personNameSuggestions, setPersonNameSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Partial returns state for validation
  const [partialReturns, setPartialReturns] = useState<any[]>([]);

  // Check if the record's account is hidden/inactive when editing
  const isAccountHidden = record && record.account_id 
    ? !accounts.find(acc => acc.id === record.account_id)?.isActive 
    : false;

  // Responsive: stack fields vertically on mobile
  const fieldRowClass = 'flex flex-col sm:flex-row gap-2 sm:gap-x-4 sm:items-center';
  const fieldColClass = 'flex-1';

  // Filter and sort accounts: only active accounts (excluding DPS), but include current account if editing
  const sortedAccountOptions = React.useMemo(() => {
    // Get active accounts (excluding DPS accounts)
    let availableAccounts = accounts.filter(account => account.isActive && !account.name.includes('(DPS)'));
    
    // If editing a record, include its current account even if hidden/inactive
    if (record?.account_id) {
      const currentAccount = accounts.find(acc => acc.id === record.account_id);
      if (currentAccount && !currentAccount.name.includes('(DPS)')) {
        // Add the current account if it's not already in the list
        const accountExists = availableAccounts.some(acc => acc.id === currentAccount.id);
        if (!accountExists) {
          availableAccounts = [...availableAccounts, currentAccount];
        }
      }
    }
    
    // Sort: default currency first, then by currency alphabetically, then by balance (descending)
    const defaultCurrency = profile?.local_currency || 'USD';
    return availableAccounts.sort((a, b) => {
      // Default currency first
      if (a.currency === defaultCurrency && b.currency !== defaultCurrency) return -1;
      if (a.currency !== defaultCurrency && b.currency === defaultCurrency) return 1;
      // Then sort by currency alphabetically
      if (a.currency !== b.currency) {
        return a.currency.localeCompare(b.currency);
      }
      // Within same currency, sort by balance (descending - highest first)
      return (b.calculated_balance || 0) - (a.calculated_balance || 0);
    });
  }, [accounts, record?.account_id, profile?.local_currency]);

  // The record's own currency may no longer match any account, so keep it listed
  // to avoid silently dropping it when the record is edited.
  const currencyOptions = React.useMemo(() => {
    const currencies = new Set(accounts.map(acc => acc.currency));
    if (form.currency) currencies.add(form.currency);
    return Array.from(currencies).sort().map(currency => ({
      value: currency,
      label: `${currency} - ${getCurrencySymbol(currency)}`
    }));
  }, [accounts, form.currency]);

  // Auto-set currency when account is selected. Record-only entries own their
  // currency and must not inherit it from an account.
  useEffect(() => {
    if (form.affect_account_balance && form.account_id) {
      const selectedAccount = accounts.find(acc => acc.id === form.account_id);
      if (selectedAccount) {
        setForm(prev => prev.currency === selectedAccount.currency
          ? prev
          : { ...prev, currency: selectedAccount.currency });
      }
    }
  }, [form.affect_account_balance, form.account_id, accounts]);

  // Autofocus first field on open
  useEffect(() => {
    if (typeRef.current) {
      typeRef.current.focus();
    }
  }, []);

  // Initialize original amount when editing
  useEffect(() => {
    if (record && record.amount) {
      setOriginalAmount(record.amount);
      setAmountMode('set');
      setAmountInput(String(record.amount));
    } else if (!record) {
      setOriginalAmount(null);
      setAmountMode('set');
      setAmountInput('');
    }
  }, [record?.id]);

  // Fetch partial returns when editing
  useEffect(() => {
    if (record?.id) {
      const fetchPartialReturns = async () => {
        try {
          const { data, error } = await supabase
            .from('lend_borrow_returns')
            .select('*')
            .eq('lend_borrow_id', record.id)
            .order('created_at', { ascending: false });
          if (error) throw error;
          setPartialReturns(data || []);
        } catch (error) {
          console.error('Error fetching partial returns:', error);
          setPartialReturns([]);
        }
      };
      fetchPartialReturns();
    } else {
      setPartialReturns([]);
    }
  }, [record?.id]);

  // Helper function to parse amount input and calculate final amount
  const parseAmountInput = (input: string): { displayValue: string; finalAmount: number | null } => {
    if (!input.trim()) {
      return { displayValue: '', finalAmount: null };
    }

    if (amountMode === 'set') {
      // In set mode, just return the parsed value
      const parsed = parseFloat(input);
      return { displayValue: input, finalAmount: isNaN(parsed) ? null : parsed };
    } else {
      // In adjust mode, parse for +28, -28, or plain numbers
      const trimmed = input.trim();
      let adjustment = 0;
      
      if (trimmed.startsWith('+') || trimmed.startsWith('-')) {
        // Parse +28 or -28
        adjustment = parseFloat(trimmed);
      } else {
        // Plain number is treated as adjustment
        adjustment = parseFloat(trimmed);
      }
      
      if (isNaN(adjustment) || originalAmount === null) {
        return { displayValue: input, finalAmount: null };
      }
      
      const finalAmount = originalAmount + adjustment;
      return { displayValue: input, finalAmount: finalAmount >= 0 ? finalAmount : null };
    }
  };

  // Get the calculated final amount for display
  const getCalculatedAmount = (): number | null => {
    if (!amountInput || !amountInput.trim()) return null;
    const result = parseAmountInput(amountInput);
    return result.finalAmount;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Prevent editing records with hidden accounts
    if (isAccountHidden) {
      newErrors.account_id = 'Cannot edit record with hidden account. Please activate the account first.';
      setErrors(newErrors);
      return false;
    }
    
    if (!form.person_name.trim()) {
      newErrors.person_name = 'Person name is required';
    }
    if (!form.amount || form.amount <= 0) {
      newErrors.amount = 'Valid amount is required';
    }
    if (!form.type) {
      newErrors.type = 'Type is required';
    }
    if (form.affect_account_balance && !form.account_id) {
      newErrors.account_id = 'Account is required when affecting account balance';
    }
    if (!form.affect_account_balance && !form.currency) {
      newErrors.currency = 'Currency is required for record-only transactions';
    }
    
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  // Inline validation on blur
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateForm();
  };

  const handleDropdownBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateForm();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'amount' ? Number(value) : value }));
    if (errors[name]) setErrors((prev: Record<string, string>) => ({ ...prev, [name]: '' }));
    
    // Handle autocomplete for person name
    if (name === 'person_name') {
      generatePersonNameSuggestions(value);
    }
  };

  const handleDropdownChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
  };

  // Generate person name suggestions
  const generatePersonNameSuggestions = (input: string) => {
    if (!input.trim()) {
      setPersonNameSuggestions([]);
      setShowPersonNameSuggestions(false);
      return;
    }

    const suggestions = lendBorrowRecords
      .map(record => record.person_name)
      .filter((name, index, self) => 
        name.toLowerCase().includes(input.toLowerCase()) && 
        self.indexOf(name) === index
      )
      .slice(0, 5);

    setPersonNameSuggestions(suggestions);
    setShowPersonNameSuggestions(suggestions.length > 0);
    setSelectedSuggestionIndex(-1);
  };

  // Handle keyboard navigation for suggestions
  const handlePersonNameKeyDown = (e: React.KeyboardEvent) => {
    if (!showPersonNameSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < personNameSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev > 0 ? prev - 1 : personNameSuggestions.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0) {
        const suggestion = personNameSuggestions[selectedSuggestionIndex];
        setForm(prev => ({ ...prev, person_name: suggestion }));
        setShowPersonNameSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowPersonNameSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setForm(prev => ({ ...prev, person_name: suggestion }));
    setShowPersonNameSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleClear = (field: 'person_name' | 'notes') => {
    setForm((prev) => ({ ...prev, [field]: '' }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setTouched((prev) => ({ ...prev, [field]: false }));
    if (field === 'person_name' && personNameRef.current) personNameRef.current.focus();
    if (field === 'notes' && notesRef.current) notesRef.current.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ person_name: true, amount: true, type: true });
    
    // Prevent editing settled records
    if (record && record.status === 'settled') {
      toast.error('Cannot edit a settled record');
      return;
    }

    // Validate amount >= total partial returns when editing
    if (record && form.amount) {
      const totalReturned = partialReturns.reduce((sum, ret) => sum + ret.amount, 0) + (record.partial_return_amount || 0);
      if (form.amount < totalReturned) {
        toast.error(`Amount cannot be less than total partial returns (${getCurrencySymbol(form.currency || 'USD')}${totalReturned.toFixed(2)})`);
        setErrors(prev => ({ ...prev, amount: `Amount must be at least ${getCurrencySymbol(form.currency || 'USD')}${totalReturned.toFixed(2)}` }));
        return;
      }
    }

    // Warn about account_id changes for records with transactions
    if (record && record.account_id && record.transaction_id && form.account_id !== record.account_id) {
      const confirmed = window.confirm('Changing the account for a record with existing transactions may cause inconsistencies. Are you sure you want to continue?');
      if (!confirmed) {
        return;
      }
    }
    
    // Auto-set due date to 7 days from today if not provided (for all records)
    let updatedForm = { ...form };

    if (form.affect_account_balance) {
      // Ensure currency is set from the selected account (only for account-linked records)
      const selectedAccount = accounts.find(acc => acc.id === form.account_id);
      if (selectedAccount) {
        updatedForm = { ...updatedForm, currency: selectedAccount.currency };
      }
    } else {
      updatedForm = { ...updatedForm, account_id: '' };
    }

    if (!form.due_date || form.due_date === '') {
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      const dueDateString = sevenDaysFromNow.getFullYear() + '-' + 
        String(sevenDaysFromNow.getMonth() + 1).padStart(2, '0') + '-' + 
        String(sevenDaysFromNow.getDate()).padStart(2, '0');
      updatedForm = { ...updatedForm, due_date: dueDateString };
    }
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    try {
      // Add a small delay to ensure loading animation is visible
      await new Promise(resolve => setTimeout(resolve, 500));
      await onSubmit(updatedForm);
      // Add a small delay before closing to show success state
      await new Promise(resolve => setTimeout(resolve, 300));
      onClose();
    } catch (error) {
      console.error('❌ Form submit error:', error);

      
      // Check if it's a plan limit error and show upgrade prompt
      if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        const errorMessage = error.message;
        
        if (errorMessage && errorMessage.includes('FEATURE_NOT_AVAILABLE') && errorMessage.includes('lend & borrow')) {
          toast.error('Lend & borrow tracking is a Premium feature. Upgrade to Premium to track loans and borrowings.');
          setTimeout(() => {
            window.location.href = '/settings?tab=plans';
          }, 2000);
          
          return;
        }
      }
      
      toast.error('Failed to save record. Please try again.');
    }
  };

  const getInputClasses = (fieldName: string) => {
    const baseClasses = "w-full px-4 py-2 text-[14px] h-10 rounded-lg border transition-colors duration-200 bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600";
    const errorClasses = "border-red-500 ring-2 ring-red-200 focus:ring-red-500 focus:border-red-500 dark:border-red-600";
    const normalClasses = "border-gray-200 focus:ring-blue-500";
    return `${baseClasses} ${errors[fieldName] && touched[fieldName] ? errorClasses : normalClasses}`;
  };

  // DatePicker: highlight today, allow typing, quick-select today
  const today = new Date();
  const handleDateChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      handleDropdownChange('due_date', `${year}-${month}-${day}`);
    } else {
      handleDropdownChange('due_date', '');
    }
  };

  // Disable Add button if required fields missing or submitting
  const isFormValid = form.person_name.trim() && form.amount && form.amount > 0 && form.type && 
    (form.affect_account_balance ? form.account_id : form.currency);

  return (
    <>
      <Loader isLoading={isLoading} message="Saving lend/borrow..." />
      <div className="fixed inset-0 flex items-center justify-center z-50">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        {/* Modal Container */}
        <div
          className={`relative bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-[38rem] max-h-[90vh] overflow-y-auto z-50 shadow-2xl transition-all ${isMobile ? 'pb-32' : ''}`}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {record ? t('lendBorrow.editLendBorrow') : t('lendBorrow.addLendBorrow')}
              </h2>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close form"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Alert banner for hidden account */}
          {isAccountHidden && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
              <EyeOff className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  This record belongs to a hidden account.
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Please activate the account in Account Settings to edit this record.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Account Balance Toggle */}
            <div className="w-full" style={{ marginTop: 0, marginBottom: '15px' }}>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-300 shadow-sm ${
                      form.affect_account_balance 
                        ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-md' 
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md'
                    }`}
                    onClick={() => {
                      setForm(prev => ({ ...prev, affect_account_balance: true }));
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white text-sm">
                        From Account
                      </div>
                      <div className="text-gray-600 dark:text-gray-400" style={{ fontSize: '10px' }}>
                        Affects Balance
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-300 shadow-sm ${
                      !form.affect_account_balance 
                        ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-md' 
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md'
                    }`}
                    onClick={() => {
                      setForm(prev => ({ ...prev, affect_account_balance: false, account_id: '' }));
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white text-sm">
                        Record Only
                      </div>
                      <div className="text-gray-600 dark:text-gray-400" style={{ fontSize: '10px' }}>
                        No Balance Change
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning Message for Record Only */}
            {!form.affect_account_balance && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3" style={{ marginBottom: '15px' }}>
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0" />
                  <span className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Record Only Mode:</strong> This record won't affect account balances. You must manually select the currency for this transaction.
                  </span>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className={fieldRowClass} style={{ marginTop: 0 }}>
              <div className={fieldColClass}>
                <CustomDropdown
                  value={form.type}
                  onChange={(value) => handleDropdownChange('type', value)}
                  options={[
                    { value: 'lend', label: t('lendBorrow.lend') },
                    { value: 'borrow', label: t('lendBorrow.borrow') },
                  ]}
                  placeholder="Type *"
                  disabled={isAccountHidden || !!record}
                />
                {errors.type && touched.type ? (
                  <p className="mt-1 text-xs text-red-600 flex items-center min-h-[20px]">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.type}
                  </p>
                ) : (
                  <div className="min-h-[20px]" />
                )}
              </div>

              <div className={fieldColClass + ' relative'}>
                <input
                  ref={personNameRef}
                  name="person_name"
                  value={form.person_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={handlePersonNameKeyDown}
                  onFocus={() => {
                    if (form.person_name.trim()) {
                      generatePersonNameSuggestions(form.person_name);
                    }
                  }}
                  className={getInputClasses('person_name') + ' min-w-[200px] pr-8'}
                  placeholder="Enter person's name *"
                  autoComplete="off"
                  autoFocus={!record}
                  disabled={isAccountHidden}
                />
                {form.person_name && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => handleClear('person_name')}
                    tabIndex={-1}
                    aria-label="Clear person name"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                
                {/* Autocomplete suggestions */}
                {showPersonNameSuggestions && personNameSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {personNameSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`px-4 py-2 cursor-pointer text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          index === selectedSuggestionIndex ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {(() => {
                          const query = form.person_name.trim();
                          const matchIndex = suggestion.toLowerCase().indexOf(query.toLowerCase());
                          if (matchIndex < 0) return suggestion;
                          const before = suggestion.slice(0, matchIndex);
                          const match = suggestion.slice(matchIndex, matchIndex + query.length);
                          const after = suggestion.slice(matchIndex + query.length);
                          return (
                            <span>
                              {before}
                              <span className="font-semibold text-blue-700 dark:text-blue-300">{match}</span>
                              {after}
                            </span>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                )}
                
                {errors.person_name && touched.person_name ? (
                  <p className="mt-1 text-xs text-red-600 flex items-center min-h-[20px]">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.person_name}
                  </p>
                ) : (
                  <div className="min-h-[20px]" />
                )}
              </div>
            </div>

            {/* Account Selection and Amount - Side by Side */}
            <div className={fieldRowClass} style={{ marginTop: 0 }}>
              <div className={fieldColClass}>
                {form.affect_account_balance ? (
                  <CustomDropdown
                    value={form.account_id ?? ''}
                    onChange={(value) => handleDropdownChange('account_id', value)}
                    options={sortedAccountOptions
                      .map(account => ({
                        value: account.id,
                        label: `${account.name} (${getCurrencySymbol(account.currency)}${Number(account.calculated_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
                      }))
                    }
                    placeholder="Select account *"
                    disabled={isAccountHidden}
                  />
                ) : (
                  <CustomDropdown
                    value={form.currency}
                    onChange={(value) => handleDropdownChange('currency', value)}
                    options={currencyOptions}
                    placeholder="Select currency *"
                    disabled={isAccountHidden}
                  />
                )}
                {errors.account_id && touched.account_id ? (
                  <p className="mt-1 text-xs text-red-600 flex items-center min-h-[20px]">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.account_id}
                  </p>
                ) : errors.currency && touched.currency ? (
                  <p className="mt-1 text-xs text-red-600 flex items-center min-h-[20px]">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.currency}
                  </p>
                ) : (
                  <div className="min-h-[20px]" />
                )}
              </div>

              <div className={fieldColClass + ' relative'}>
                <input
                  name="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount || ''}
                  onChange={handleChange}
                  onClick={(e) => {
                    // Open modal when clicking on amount field (only when editing)
                    if (record && record.amount && !isAccountHidden) {
                      e.preventDefault();
                      setShowAmountModal(true);
                    }
                  }}
                  onBlur={handleBlur}
                  className={getInputClasses('amount') + ` min-w-[150px] ${record && record.amount ? 'cursor-pointer' : ''}`}
                  placeholder="0.00 *"
                  autoComplete="off"
                  readOnly={(record && record.amount) || isAccountHidden ? true : false}
                  disabled={isAccountHidden}
                />
                {record && record.amount && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-600 dark:text-blue-400">
                    Click to edit
                  </div>
                )}
                {errors.amount && touched.amount ? (
                  <p className="mt-1 text-xs text-red-600 flex items-center min-h-[20px]">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.amount}
                  </p>
                ) : (
                  <div className="min-h-[20px]" />
                )}
              </div>
            </div>

            {/* Due Date - Show for all records */}
            <div className="w-full" style={{ marginTop: 0, marginBottom: '15px' }}>
              <div className={getInputClasses('due_date') + ' flex items-center bg-gray-100 dark:bg-gray-700 px-4 pr-[10px] text-[14px] h-10 rounded-lg w-full'}>
                <svg className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                <DatePicker
                  selected={parseLocalDate(form.due_date)}
                  onChange={handleDateChange}
                  onBlur={() => handleDropdownBlur('due_date')}
                  placeholderText="Due date"
                  dateFormat="yyyy-MM-dd"
                  className="bg-transparent outline-none border-none w-full cursor-pointer text-[14px] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400"
                  todayButton="Today"
                  highlightDates={[today]}
                  isClearable
                  autoComplete="off"
                  disabled={isAccountHidden}
                />
              </div>
              {errors.due_date && touched.due_date && (
                <p className="mt-1 text-xs text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.due_date}
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="relative" style={{ marginTop: 0 }}>
              <textarea
                ref={notesRef}
                name="notes"
                value={form.notes}
                onChange={handleChange}
                onBlur={handleBlur}
                className={getInputClasses('notes') + ' w-full resize-none h-[100px] pr-8'}
                placeholder="Add any notes, description, or additional details about this transaction"
                disabled={isAccountHidden}
              />
              {form.notes && (
                <button
                  type="button"
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  onClick={() => handleClear('notes')}
                  tabIndex={-1}
                  aria-label="Clear notes"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {errors.notes && touched.notes && (
                <p className="mt-1 text-xs text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.notes}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-gradient-primary text-white rounded-lg hover:bg-gradient-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[80px]"
                disabled={isLoading || !isFormValid || isAccountHidden}
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {record ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Amount Adjustment Modal */}
      {record && record.amount && (
        <AmountAdjustmentModal
          isOpen={showAmountModal}
          onClose={() => setShowAmountModal(false)}
          currentAmount={record.amount}
          onConfirm={(newAmount) => {
            setForm(prev => ({ ...prev, amount: newAmount }));
            setShowAmountModal(false);
          }}
          currencySymbol={form.affect_account_balance && form.account_id
            ? getCurrencySymbol(accounts.find(acc => acc.id === form.account_id)?.currency || 'USD')
            : getCurrencySymbol(form.currency || 'USD')}
          label="Amount"
        />
      )}
    </>
  );
}; 

